@for (block of contentBlocks; track $index) {
  @switch (block.type) {
    @case ('heading') {
      @if (block.level === 1) {
        <h1 [innerHTML]="(block.text ?? '') | formatChat"></h1>
      } @else if (block.level === 2) {
        <h2 [innerHTML]="(block.text ?? '') | formatChat"></h2>
      } @else {
        <h3 [innerHTML]="(block.text ?? '') | formatChat"></h3>
      }
    }
    @case ('paragraph') {
      <p [innerHTML]="(block.text ?? '') | formatChat"></p>
    }
    @case ('link') {
      <p><a [href]="block.url" class="link" target="_blank" rel="noopener noreferrer">{{ block.text }}</a></p>
    }
    @case ('list') {
      @if(!block.ordered) {
        <ul>
          @for (item of block.items; track item) {
            <li [innerHTML]="$any(item) | formatChat"></li>
          }
        </ul>
      } @else {
        <ol>
          @for (item of block.items; track item) {
            <li [innerHTML]="$any(item) | formatChat"></li>
          }
        </ol>
      }
    }
    @case ('divider') {
      <hr class="my-4" />
    }

    @case ('image') {
      <img [src]="block.url" [alt]="block.alt" class="img-fluid my-2" />
    }

    @case ('quote') {
      <blockquote class="blockquote">
        <p>"{{ block.text }}"</p>
        <footer class="blockquote-footer">{{ block.author }}</footer>
      </blockquote>
    }

    @case ('chart') {
      <div class="chart-container my-4">
        <h5>{{ block.title }}</h5>
        @if (block.chartType === 'pie'){
          <apx-chart
            [series]="$any(block.data).datasets[0].data"
            [chart]="chartOptions.chart ? { type: $any(block.chartType), foreColor: chartOptions.chart.foreColor, toolbar: chartOptions.chart.toolbar } : { type: $any(block.chartType) }"
            [labels]="$any(block.data).labels"
            [colors]="chartColors"
            [tooltip]="$any(chartOptions.tooltip)"
            [dataLabels]="$any(chartOptions.dataLabels)"
            [legend]="$any(chartOptions.legend)">
          </apx-chart>
        } @else {
          <apx-chart
            [series]="[{ name: $any(block.data).datasets[0].label, data: $any(block.data).datasets[0].data }]"
            [chart]="chartOptions.chart ? { type: $any(block.chartType), foreColor: chartOptions.chart.foreColor, toolbar: chartOptions.chart.toolbar } : { type: $any(block.chartType) }"
            [colors]="chartColors"
            [xaxis]="{ categories: $any(block.data).labels, labels: chartOptions.xaxis?.labels }"
            [yaxis]="$any(chartOptions.yaxis)"
            [tooltip]="$any(chartOptions.tooltip)"
            [dataLabels]="$any(chartOptions.dataLabels)"
            [grid]="$any(chartOptions.grid)"
            [legend]="$any(chartOptions.legend)">
          </apx-chart>
        }
      </div>
    }

  @case ('table') {
    <div class="table-responsive">
      @if (block.caption){
        <h5>{{ block.caption }}</h5>
      }
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
          @for (header of block.headers; track header) {
          <th>{{ header }}</th>
          }
        </tr>
      </thead>
      <tbody>
        @for (row of block.rows; track $index; let rowIndex = $index) {
        <tr>
          @for (cell of row; track $index; let cellIndex = $index) {
          <td>{{ cell }}</td>
          }
        </tr>
        }
      </tbody>
    </table>
  </div>
  }

  @case ('tee-time-picker') {
    <app-book-tee-time [data]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (timeSelected)="onPickerTimeSelected($event)" />
  }

  @case ('tee-time-booking-form') {
    <app-book-tee-time [data]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      mode="search"
      (timeSelected)="onPickerTimeSelected($event)" />
  }

  @case ('booking-review') {
    <app-tee-time-detail [review]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('booking-confirmation') {
    <app-tee-time-detail [confirmation]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('cancellation-confirmation') {
    <app-tee-time-detail [cancellation]="$any(block.data)" [embedded]="true" />
  }

  @case ('waitlist-review') {
    <app-tee-time-detail [waitlistReview]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('waitlist-form') {
    <app-waitlist-form [data]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('waitlist-confirmation') {
    <app-tee-time-detail [waitlistConfirmation]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('alert-form') {
    <app-alert-form [data]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('alert-confirmation') {
    <app-alert-confirmation [data]="$any(block.data)" [embedded]="true" [interactive]="interactive"
      (action)="onPickerTimeSelected($event)" />
  }

  @case ('profile-verification-required') {
    <app-profile-verification-required [data]="$any(block.data)" [interactive]="interactive" />
  }

  @case ('tee-time-list') {
    @let teeTimeData = $any(block.data);
    @let openCourse = getCurrentCourse(teeTimeData);
    @if (openCourse) {
      <!-- Detail view (carousel) -->
      <div class="course-detail">
        <div class="course-detail__nav">
          <span class="course-detail__count">
            {{ getCurrentCourseIndex(teeTimeData) + 1 }} of {{ teeTimeData.courses.length }} courses found
          </span>
          <div class="course-detail__arrows">
            <button type="button" class="course-detail__arrow"
                    aria-label="Previous course"
                    [disabled]="!interactive || !hasPrevCourse(teeTimeData)"
                    (click)="prevCourse(teeTimeData)">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m15 18-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <button type="button" class="course-detail__arrow"
                    aria-label="Next course"
                    [disabled]="!interactive || !hasNextCourse(teeTimeData)"
                    (click)="nextCourse(teeTimeData)">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        @for (c of [openCourse]; track c.locationId) {
          <div class="course-detail__header">
            <div class="course-detail__image"
                 [style.background-image]="c.imageUrl ? 'url(' + c.imageUrl + ')' : null"
                 [class.course-detail__image--placeholder]="!c.imageUrl">
              @if (!c.imageUrl) {
                <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 3v18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                  <path d="M6 4h12l-3 4 3 4H6" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                </svg>
              }
              @if (c.verified) {
                <img class="course-detail__badge" src="assets/images/golf-ai-verified-badge.png" alt="GOLF.AI Verified course" />
              }
            </div>
            <div class="course-detail__heading">
              @if (getCountryFlagCode(c); as code) {
                <img class="course-detail__flag" [src]="'flags/' + code + '.svg'" width="28" height="21" alt="" aria-hidden="true" />
              }
              <h3 class="course-detail__name">{{ c.name }}</h3>
              @if (c.city || c.country) {
                <p class="course-detail__location">
                  {{ c.city }}{{ c.city && c.country ? ', ' : '' }}{{ c.country }}
                </p>
              }
            </div>
          </div>

          <app-book-tee-time
            [data]="buildPickerData(c, teeTimeData)"
            [embedded]="true"
            [interactive]="interactive"
            mode="detail"
            (timeSelected)="onPickerTimeSelected($event)" />
        }
      </div>
    } @else {
      <!-- List view -->
      @if (teeTimeData?.message) {
        <p class="teetime-message">{{ teeTimeData.message }}</p>
      }
      @if (teeTimeData?.totalCount) {
        <p class="teetime-count">
          {{ teeTimeData.totalCount }} {{ teeTimeData.totalCount === 1 ? 'Course' : 'Courses' }}
          @if (teeTimeData.withinKm) {
            within {{ teeTimeData.withinKm }}km
          }
          found
        </p>
      }
      <div class="course-card-list">
        @for (course of teeTimeData?.courses; track course.locationId) {
          <button type="button" class="course-card"
                  [disabled]="!interactive"
                  (click)="onSelectCourse(course)">
            <div class="course-card__image"
                 [style.background-image]="course.imageUrl ? 'url(' + course.imageUrl + ')' : null"
                 [class.course-card__image--placeholder]="!course.imageUrl">
              @if (!course.imageUrl) {
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 3v18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                  <path d="M6 4h12l-3 4 3 4H6" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                </svg>
              }
              @if (course.verified) {
                <img class="course-card__badge" src="assets/images/golf-ai-verified-badge.png" alt="GOLF.AI Verified course" />
              }
            </div>
            <div class="course-card__body">
              @if (getCountryFlagCode(course); as code) {
                <img class="course-card__flag" [src]="'flags/' + code + '.svg'" width="29" height="22" alt="" aria-hidden="true" />
              }
              <h4 class="course-card__name">{{ course.name }}</h4>
              @if (course.city || course.country) {
                <p class="course-card__location">
                  {{ course.city }}{{ course.city && course.country ? ', ' : '' }}{{ course.country }}
                </p>
              }
              @if (course.distanceKm != null || course.priceRange) {
                <p class="course-card__meta">
                  @if (course.distanceKm != null) {
                    <span>{{ course.distanceKm | number:'1.0-0' }} km away</span>
                  }
                  @if (course.priceRange) {
                    <span>{{ course.priceRange }}</span>
                  }
                </p>
              }
            </div>
          </button>
        }
      </div>
    }
  }

  } <!-- end @switch -->
} <!-- end @for -->

@if (loading) {
<div class="loadingItem">
  <p class="mb-0">
    <i>ssssssssssssssssssssssss</i>
    <i class="ms-1">ssssssssssssssss</i>
    <i>ssssssssssss</i>
    <i class="ms-1">ssssssssssssssss</i>
    <i class="ms-1">sssssssssssssss</i>
  </p>
</div>
}